import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/lib/contexts/auth-context";
import { useFeatureFlag, FeatureFlags } from "@/lib/contexts/feature-flags";
import { LogOut, Menu, Plus, Edit, Settings, CreditCard, Gift } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RedeemGiftCardDialog } from "@/components/gift-cards/redeem-gift-card-dialog";
import { NotificationBell } from "./notification-bell";
import { CreatePicker } from "./create-picker";

function getInitials(name?: string | null, email?: string | null): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }
  return "U";
}

export function Header() {
  const { user, profile, signOut, hasStudioAccess } = useAuth();
  const isClassesEnabled = useFeatureFlag(FeatureFlags.ENABLE_STUDIO_CLASSES);
  const [open, setOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [createPickerOpen, setCreatePickerOpen] = useState(false);
  const [avatarDropdownOpen, setAvatarDropdownOpen] = useState(false);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const navigate = useNavigate();

  const avatarUrl = profile?.profile_photo || user?.user_metadata?.avatar_url;
  const displayName = profile?.full_name || user?.user_metadata?.full_name || null;
  const displayEmail = profile?.email || user?.email || null;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setOpen(false);
    navigate("/signin");
  };

  const closeSheet = () => setOpen(false);

  return (
    <header className={`sticky top-0 z-50 transition-colors duration-300 ${
      isScrolled
        ? "bg-on-light"
        : ""
    }`}>
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        {/* Left - Logo */}
        <Link to="/" className="flex items-center pt-1">
          <img
            src={isScrolled ? "/dnceclub-logo-diamond.svg" : "/dnceclub-logo-off-black.svg"}
            alt="Dnceclub"
            className="w-20 md:w-24 h-auto transition-opacity duration-300"
          />
        </Link>

        {/* Center - Main Navigation */}
        <nav className="hidden md:flex items-center justify-center gap-6 absolute left-1/2 -translate-x-1/2">
          {/* Dashboard - first item for logged-in non-owners */}
          {user && !hasStudioAccess && (
            <Link
              to="/dashboard"
              className={`text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                isScrolled ? "text-on-dark" : "text-on-light"
              }`}
            >
              DASHBOARD
            </Link>
          )}
          <Link
            to="/studios"
            className={`text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
              isScrolled ? "text-on-dark" : "text-on-light"
            }`}
          >
           BROWSE STUDIOS
          </Link>
          {!user && (
            <Link
              to="/classes"
              className={`text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                isScrolled ? "text-on-dark" : "text-on-light"
              }`}
            >
              BROWSE CLASSES
            </Link>
          )}

          {/* Renter links - for ALL logged-in users */}
          {user && (
            <>
              <Link
                to="/classes"
                className={`text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                  isScrolled ? "text-on-dark" : "text-on-light"
                }`}
              >
                BROWSE CLASSES
              </Link>

              {/* Studio Portal link - centered, for owners and staff */}
              {hasStudioAccess && (
                <Link
                  to="/owner/dashboard"
                  className={`text-sm font-medium whitespace-nowrap transition-all duration-300 px-3 py-1.5 rounded-full border ${
                    isScrolled
                      ? "text-on-dark border-on-dark/20 bg-white/10"
                      : "text-on-light border-on-light/20 bg-black/5"
                  }`}
                >
                  STUDIO PORTAL
                </Link>
              )}

              <Link
                to="/rentals"
                className={`text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                  isScrolled ? "text-on-dark" : "text-on-light"
                }`}
              >
                MY RENTALS
              </Link>
              <Link
                to={profile?.username ? `/${profile.username}` : "/profile"}
                className={`text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                  isScrolled ? "text-on-dark" : "text-on-light"
                }`}
              >
                MY PROFILE
              </Link>
            </>
          )}
        </nav>

        {/* Right - Auth Buttons */}
        <div className="hidden md:flex items-center justify-end gap-3">
          {user ? (
            <>
              {/* + Create picker - for ALL logged-in users */}
              <Button
                size="sm"
                variant={isScrolled ? "outline" : "outline-on-light"}
                className="gap-1.5"
                onClick={() => {
                  setAvatarDropdownOpen(false);
                  setCreatePickerOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
                Create
              </Button>

              {/* Notification bell */}
              <NotificationBell isScrolled={isScrolled} />

              {/* Avatar dropdown */}
              <DropdownMenu open={avatarDropdownOpen} onOpenChange={(v) => {
                setAvatarDropdownOpen(v);
                if (v) setCreatePickerOpen(false);
              }}>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="avatar-menu-trigger"
                    className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <Avatar className="h-9 w-9 cursor-pointer">
                      <AvatarImage
                        src={avatarUrl || undefined}
                        alt={displayName || "User avatar"}
                      />
                      <AvatarFallback className="bg-on-light text-on-light text-sm font-medium">
                        {getInitials(displayName, displayEmail)}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 !left-auto !right-0">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-on-dark">
                        {displayName || "User"}
                      </p>
                      <p className="text-xs text-on-dark-muted">
                        {displayEmail}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
                    <Edit className="mr-2 h-4 w-4" />
                    Edit Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/class-settings")} className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Class Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/pro")} className="cursor-pointer">
                    <span className="mr-2 inline-flex h-4 w-4 items-center justify-center text-sm">✨</span>
                    Dnceclub Pro
                  </DropdownMenuItem>
                  {isClassesEnabled && (
                    <DropdownMenuItem onClick={() => navigate("/memberships")} className="cursor-pointer">
                      <CreditCard className="mr-2 h-4 w-4" />
                      Studio Memberships
                    </DropdownMenuItem>
                  )}
                  {isClassesEnabled && (
                    <DropdownMenuItem onClick={() => setRedeemDialogOpen(true)} className="cursor-pointer">
                      <Gift className="mr-2 h-4 w-4" />
                      Redeem Gift Card
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <RedeemGiftCardDialog open={redeemDialogOpen} onOpenChange={setRedeemDialogOpen} />
            </>
          ) : (
            <>
              <Link to="/signin">
                <Button
                  variant={isScrolled ? "outline" : "outline-on-light"}
                  size="sm"
                >
                  SIGN IN
                </Button>
              </Link>
              <Link to="/signup">
                <Button
                variant={isScrolled ? "default" : "default-on-light"}
                  size="sm"
                >
                  SIGN UP
                </Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden flex items-center justify-end gap-1">
          {/* + Create picker (mobile) - for ALL logged-in users */}
          {user && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 hover:bg-black/5 dark:hover:bg-white/10"
              onClick={() => setCreatePickerOpen(true)}
            >
              <Plus className={`h-4 w-4 transition-colors duration-300 ${
                isScrolled ? "text-on-dark" : "text-on-light"
              }`} />
            </Button>
          )}
          {/* Notification bell (mobile) */}
          {user && (
            <NotificationBell isScrolled={isScrolled} />
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className={`h-5 w-5 transition-colors duration-300 ${
                  isScrolled ? "text-on-dark hover:text-on-dark-muted" : "text-on-light hover:text-on-light-muted"
                }`} />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
          <SheetContent side="right" className="w-[240px] bg-on-light">
            <nav className="flex flex-col gap-4">
              {/* Dashboard - first item for logged-in non-owners */}
              {user && !hasStudioAccess && (
                <Link
                  to="/dashboard"
                  onClick={closeSheet}
                  className="text-lg py-2 text-on-dark"
                >
                  DASHBOARD
                </Link>
              )}
              <Link
                to="/studios"
                onClick={closeSheet}
                className="text-lg py-2 text-on-dark"
              >
                BROWSE STUDIOS
              </Link>
              {!user && (
                <Link
                  to="/classes"
                  onClick={closeSheet}
                  className="text-lg py-2 text-on-dark"
                >
                  BROWSE CLASSES
                </Link>
              )}

              {user ? (
                <>
                  {/* Studio Portal link - for owners and staff */}
                  {hasStudioAccess && (
                    <Link
                      to="/owner/dashboard"
                      onClick={closeSheet}
                      className="text-lg py-2 px-3 -mx-3 rounded-lg text-on-dark bg-white/10 border border-white/10 w-fit"
                    >
                      STUDIO PORTAL
                    </Link>
                  )}
                  {/* Renter links - for ALL logged-in users */}
                  <Link
                    to="/classes"
                    onClick={closeSheet}
                    className="text-lg py-2 text-on-dark"
                  >
                    BROWSE CLASSES
                  </Link>
                  <Link
                    to="/rentals"
                    onClick={closeSheet}
                    className="text-lg py-2 text-on-dark"
                  >
                    MY RENTALS
                  </Link>
                  <Link
                    to={profile?.username ? `/${profile.username}` : "/profile"}
                    onClick={closeSheet}
                    className="text-lg py-2 text-on-dark"
                  >
                    MY PROFILE
                  </Link>
                  <Separator />
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage
                        src={avatarUrl || undefined}
                        alt={displayName || "User avatar"}
                      />
                      <AvatarFallback className="bg-on-light text-on-light text-sm font-medium">
                        {getInitials(displayName, displayEmail)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col min-w-0">
                      <p className="text-sm font-medium text-on-dark truncate">
                        {displayName || "User"}
                      </p>
                      <p className="text-xs text-on-dark-muted truncate">
                        {displayEmail}
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/profile"
                    onClick={closeSheet}
                    className="text-sm py-1.5 text-on-dark-muted"
                  >
                    EDIT PROFILE
                  </Link>
                  <Link
                    to="/pro"
                    onClick={closeSheet}
                    className="text-sm py-1.5 text-on-dark-muted"
                  >
                    DNCECLUB PRO
                  </Link>
                  <Link
                    to="/class-settings"
                    onClick={closeSheet}
                    className="text-sm py-1.5 text-on-dark-muted"
                  >
                    CLASS SETTINGS
                  </Link>
                  {isClassesEnabled && (
                    <Link
                      to="/memberships"
                      onClick={closeSheet}
                      className="text-sm py-1.5 text-on-dark-muted"
                    >
                      MEMBERSHIPS
                    </Link>
                  )}
                  {isClassesEnabled && (
                    <button
                      onClick={() => {
                        closeSheet();
                        setRedeemDialogOpen(true);
                      }}
                      className="text-sm py-1.5 text-on-dark-muted text-left"
                    >
                      REDEEM GIFT CARD
                    </button>
                  )}
                  <Button
                    onClick={handleSignOut}
                    variant="outline"
                    className="mt-2 w-full"
                  >
                    SIGN OUT
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/signin" onClick={closeSheet}>
                    <Button variant="outline" className="w-full">
                      SIGN IN
                    </Button>
                  </Link>
                  <Link to="/signup" onClick={closeSheet}>
                    <Button className="w-full" variant="default">SIGN UP</Button>
                  </Link>
                </>
              )}
            </nav>
          </SheetContent>
        </Sheet>
        </div>
      </div>
      <CreatePicker open={createPickerOpen} onOpenChange={setCreatePickerOpen} />
    </header>
  );
}
